// ============================================================
// water.js
// ------------------------------------------------------------
// สูตรคำนวณค่าน้ำ + เบี้ยปรับ + ค่าต่อกลับมิเตอร์
//
// *** ห้ามแก้ตัวเลข/ลำดับการคำนวณในไฟล์นี้โดยไม่ตรวจกับฝั่ง backend ***
// ทุกบรรทัดยกมาจาก CustomComponent.js เดิมแบบตรงตัว เพิ่มแค่คอมเมนต์อธิบาย
// ============================================================

import { GET, PUT } from "../core/api.js";
import { tokens, getStorage } from "../core/storage.js";
import { toFixed, fieldSorter, roundToTwo } from "../core/format.js";
import { billTotal } from "./water-setting.js";

// ===== คำนวณค่าน้ำ 3 แบบ =====
// setting มาจาก API v1/TH/water-manage/detail/<water_id>
//   { water_charge_start, water_charge_end, water_charge_price, water_rate_list: [{ water_rate_start, water_rate_end, water_rate_price }] }
// ตัวอย่าง setting ที่ใช้อ้างอิงตัวเลขในคอมเมนต์ด้านล่าง (ตามไฟล์ Excel ที่ลูกค้าส่งมา):
//   ขั้นต่ำ 0-4 = 70 บาท | ขั้น 5-10 = 14.7 | ขั้น 11-20 = 15 | ขั้น 21-30 = 20
//
//   แบบ 1 cal_water_bill_1 (ขั้นบันไดตั้งแต่ขั้นที่ 3) : 4→70, 10→147, 20→297, 30→497
//   แบบ 2 cal_water_bill_2 (ขั้นบันไดแท้)              : 4→70, 10→158.2, 20→308.2, 30→508.2
//   แบบ 3 cal_water_bill_3 (เหมารวม)                   : 4→70, 10→147, 20→300, 30→600

// อ่าน setting จาก API ให้เป็นตัวเลขล้วน (ตัด comma ที่มาจาก toFixed ฝั่งหน้าจอออก)
const parse_water_setting = (setting) => {
  const to_number = (value) => Number(String(value ?? 0).replace(/,/g, "")) || 0;
  return {
    charge_end: to_number(setting?.water_charge_end),
    charge_price: to_number(setting?.water_charge_price),
    tiers: (setting?.water_rate_list || [])
      .map((w) => ({
        start: to_number(w.water_rate_start),
        end: to_number(w.water_rate_end),
        price: to_number(w.water_rate_price),
      }))
      .sort((a, b) => a.start - b.start),
  };
};

// log ขั้นตอนการคิดเงินลง console (ใช้ร่วมกันทั้ง 3 แบบ) — ไว้ตรวจสอบยอดตอนลูกค้าทักท้วง
const log_water_bill = (mode, unit_used, setting, steps, result, note) => {
  console.groupCollapsed(`[${mode}] หน่วยที่ใช้ ${unit_used} -> ${toFixed(result)} บาท (${note})`);
  console.log("input:", {
    unit_used,
    water_charge_end: setting?.water_charge_end,
    water_charge_price: setting?.water_charge_price,
    water_rate_list: setting?.water_rate_list,
  });
  if (steps.length) console.table(steps);
  console.log("water_bill:", result);
  console.groupEnd();
  return result;
};

// ปัดยอดเป็นทศนิยม 2 ตำแหน่งแบบปัดครึ่งขึ้น (ใช้ตัวเดียวกับทั้งระบบ)
const round2 = (value) => roundToTwo(value);

// คิดขั้นบันไดต่อจากจุดที่คิดไปแล้ว (ใช้ร่วมกันระหว่างแบบ 1 กับแบบ 2 ที่ตรรกะช่วงท้ายเหมือนกันเป๊ะ)
//   u           = หน่วยที่ใช้จริง
//   tiers       = ขั้นราคาที่ยังต้องไล่คิด
//   start_price = ยอดเงินสะสมตั้งต้น
//   start_count = จำนวนหน่วยที่คิดเงินไปแล้ว
//   start_last_price = ราคาต่อหน่วยของขั้นล่าสุด (ใช้ตอนหน่วยเกินขั้นสุดท้าย)
// คืน { price, steps } — steps ใช้ต่อท้าย log
const accumulate_tiers = (u, tiers, start_price, start_count, start_last_price) => {
  const steps = [];
  let price = start_price;
  let counted = start_count;
  let last_price = start_last_price;

  for (const t of tiers) {
    // ยังใช้น้ำไม่ถึงขั้นนี้ → หยุดคิด
    if (u < t.start) {
      steps.push({ ขั้น: `${t.start}-${t.end}`, ช่วง: `${t.start}-${t.end}`, หน่วยที่คิด: 0, ราคาต่อหน่วย: t.price, เป็นเงิน: 0, ยอดสะสม: round2(price), หมายเหตุ: "ยังไม่ถึงขั้นนี้ หยุดคิด" });
      break;
    }
    last_price = t.price;
    const upto = Math.min(u, t.end); // คิดถึงหน่วยไหนในขั้นนี้
    const units = upto > counted ? upto - counted : 0; // จำนวนหน่วยที่ยังไม่ถูกคิดเงิน
    const amount = units * t.price;
    const from = counted;
    if (units > 0) {
      price += amount;
      counted = upto;
    }
    steps.push({ ขั้น: `${t.start}-${t.end}`, ช่วง: `${from}-${upto}`, หน่วยที่คิด: units, ราคาต่อหน่วย: t.price, เป็นเงิน: round2(amount), ยอดสะสม: round2(price) });
  }

  // ใช้น้ำเกินขั้นสุดท้ายที่ตั้งไว้ → ส่วนเกินคิดด้วยราคาขั้นสุดท้าย
  if (u > counted) {
    const units = u - counted;
    const amount = units * last_price;
    price += amount;
    steps.push({ ขั้น: "เกินขั้นสุดท้าย", ช่วง: `${counted}-${u}`, หน่วยที่คิด: units, ราคาต่อหน่วย: last_price, เป็นเงิน: round2(amount), ยอดสะสม: round2(price) });
  }

  return { price, steps };
};

// แบบ 1 : ขั้นบันไดตั้งแต่ขั้นที่ 3
//   ขั้นที่ 1 (0-4)   -> เหมาจ่ายอัตราขั้นต่ำ 70
//   ขั้นที่ 2 (5-10)  -> เหมารวม คิดราคาขั้นนี้คูณหน่วยทั้งหมด (10 หน่วย x 14.7 = 147)
//   ขั้นที่ 3 ขึ้นไป  -> ยอดเต็มของขั้นที่ 2 (147) + คิดขั้นบันไดเฉพาะหน่วยส่วนเกินของแต่ละขั้น
//   เช่น 20 หน่วย = 147 + (20-10) x 15 = 297 | 30 หน่วย = 297 + (30-20) x 20 = 497
export const cal_water_bill_1 = (unit_used, setting) => {
  const u = Number(unit_used);
  const steps = [];
  const done = (result, note) => log_water_bill("cal_water_bill_1 ขั้นบันไดตั้งแต่ขั้นที่ 3", unit_used, setting, steps, result, note);

  if (!isFinite(u) || u < 0) return done(0, "หน่วยติดลบ/ไม่ใช่ตัวเลข คิด 0");

  const { charge_end, charge_price, tiers } = parse_water_setting(setting);

  // ไม่เกินขั้นต่ำ → จ่ายอัตราเหมาขั้นต่ำอย่างเดียว
  if (u <= charge_end) {
    steps.push({ ขั้น: "ขั้นที่ 1 (ขั้นต่ำ)", ช่วง: `0-${charge_end}`, หน่วยที่คิด: u, ราคาต่อหน่วย: "เหมาจ่าย", เป็นเงิน: charge_price, ยอดสะสม: charge_price });
    return done(round2(charge_price), "ไม่เกินอัตราขั้นต่ำ");
  }

  const first = tiers[0];
  if (!first) {
    steps.push({ ขั้น: "ขั้นที่ 1 (ขั้นต่ำ)", ช่วง: `0-${charge_end}`, หน่วยที่คิด: u, ราคาต่อหน่วย: "เหมาจ่าย", เป็นเงิน: charge_price, ยอดสะสม: charge_price });
    return done(round2(charge_price), "ไม่ได้ตั้งขั้นราคาไว้ ใช้อัตราขั้นต่ำ");
  }

  // ยังอยู่ในขั้นที่ 2 -> เหมารวมทั้งหมดด้วยราคาขั้นนี้
  if (u <= first.end) {
    const amount = u * first.price;
    steps.push({ ขั้น: "ขั้นที่ 2 (เหมารวม)", ช่วง: `1-${u}`, หน่วยที่คิด: u, ราคาต่อหน่วย: first.price, เป็นเงิน: round2(amount), ยอดสะสม: round2(amount) });
    return done(round2(amount), "อยู่ในขั้นที่ 2 คิดเหมารวม");
  }

  // ขั้นที่ 3 ขึ้นไป -> ตั้งต้นด้วยยอดเต็มของขั้นที่ 2 แล้วคิดขั้นบันไดต่อ
  const base_price = first.end * first.price;
  steps.push({ ขั้น: "ขั้นที่ 2 (เหมารวมเต็มขั้น)", ช่วง: `1-${first.end}`, หน่วยที่คิด: first.end, ราคาต่อหน่วย: first.price, เป็นเงิน: round2(base_price), ยอดสะสม: round2(base_price) });

  const { price, steps: tier_steps } = accumulate_tiers(u, tiers.slice(1), base_price, first.end, first.price);
  steps.push(...tier_steps);

  return done(round2(price), "ขั้นบันไดตั้งแต่ขั้นที่ 3");
};

// แบบ 2 : ขั้นบันไดแท้ (marginal) คิดเฉพาะหน่วยส่วนเกินของแต่ละขั้น
//   0-4 = 70 | 5-10 = 6 หน่วย x 14.7 = 88.2 | 11-20 = 10 x 15 = 150 | 21-30 = 10 x 20 = 200
//   30 หน่วย = 70 + 88.2 + 150 + 200 = 508.2
export const cal_water_bill_2 = (unit_used, setting) => {
  const u = Number(unit_used);
  const steps = [];
  const done = (result, note) => log_water_bill("cal_water_bill_2 ขั้นบันไดแท้", unit_used, setting, steps, result, note);

  if (!isFinite(u) || u < 0) return done(0, "หน่วยติดลบ/ไม่ใช่ตัวเลข คิด 0");

  const { charge_end, charge_price, tiers } = parse_water_setting(setting);

  if (u <= charge_end) {
    steps.push({ ขั้น: "ขั้นต่ำ", ช่วง: `0-${charge_end}`, หน่วยที่คิด: u, ราคาต่อหน่วย: "เหมาจ่าย", เป็นเงิน: charge_price, ยอดสะสม: charge_price });
    return done(round2(charge_price), "ไม่เกินอัตราขั้นต่ำ");
  }

  // ตั้งต้นด้วยค่าขั้นต่ำ แล้วคิดขั้นบันไดต่อจากหน่วยที่เกินขั้นต่ำ
  steps.push({ ขั้น: "ขั้นต่ำ", ช่วง: `0-${charge_end}`, หน่วยที่คิด: charge_end, ราคาต่อหน่วย: "เหมาจ่าย", เป็นเงิน: charge_price, ยอดสะสม: round2(charge_price) });

  const { price, steps: tier_steps } = accumulate_tiers(u, tiers, charge_price, charge_end, 0);
  steps.push(...tier_steps);

  return done(round2(price), "คิดตามขั้นบันไดแท้");
};

// แบบ 3 : เหมารวม คิดราคาของขั้นที่หน่วยตกอยู่ คูณจำนวนหน่วยทั้งหมด
//   ตัวอย่างจากไฟล์ Excel (ขั้นต่ำ 0-4 = 70, 5-10 = 15, 11-20 = 20, 21-30 = 30)
//   10 หน่วย = 10 x 15 = 150 | 20 หน่วย = 20 x 20 = 400 | 30 หน่วย = 30 x 30 = 900
export const cal_water_bill_3 = (unit_used, setting) => {
  const u = Number(unit_used);
  const steps = [];
  const done = (result, note) => log_water_bill("cal_water_bill_3 เหมารวม", unit_used, setting, steps, result, note);

  if (!isFinite(u) || u < 0) return done(0, "หน่วยติดลบ/ไม่ใช่ตัวเลข คิด 0");

  const { charge_end, charge_price, tiers } = parse_water_setting(setting);

  if (u <= charge_end) {
    steps.push({ ขั้น: "ขั้นต่ำ", ช่วง: `0-${charge_end}`, หน่วยที่คิด: u, ราคาต่อหน่วย: "เหมาจ่าย", เป็นเงิน: charge_price, ยอดสะสม: charge_price });
    return done(round2(charge_price), "ไม่เกินอัตราขั้นต่ำ");
  }

  if (tiers.length === 0) {
    steps.push({ ขั้น: "ขั้นต่ำ", ช่วง: `0-${charge_end}`, หน่วยที่คิด: u, ราคาต่อหน่วย: "เหมาจ่าย", เป็นเงิน: charge_price, ยอดสะสม: charge_price });
    return done(round2(charge_price), "ไม่ได้ตั้งขั้นราคาไว้ ใช้อัตราขั้นต่ำ");
  }

  // ขั้นที่หน่วยตกอยู่ ถ้าเกินขั้นสุดท้ายใช้ราคาขั้นสุดท้าย
  const tier = tiers.find((t) => u >= t.start && u <= t.end) || tiers[tiers.length - 1];
  const over_last = u > tiers[tiers.length - 1].end;
  const price = u * tier.price;

  steps.push({
    ขั้น: over_last ? "เกินขั้นสุดท้าย" : `${tier.start}-${tier.end}`,
    ช่วง: `1-${u}`,
    หน่วยที่คิด: u,
    ราคาต่อหน่วย: tier.price,
    เป็นเงิน: round2(price),
    ยอดสะสม: round2(price),
  });

  return done(round2(price), over_last ? "เกินขั้นสุดท้าย คิดเหมารวมด้วยราคาขั้นสุดท้าย" : "คิดเหมารวมด้วยราคาของขั้นที่หน่วยตกอยู่");
};

// ตัวที่หน้าอื่นเรียกใช้ เลือกสูตรจาก setting.water_calculate_type (1/2/3) ค่าเริ่มต้น = แบบ 2 ขั้นบันไดแท้
export const cal_water_bill = (unit_used, setting) => {
  const type = Number(setting?.water_calculate_type) || 2;
  if (type === 1) return cal_water_bill_1(unit_used, setting);
  if (type === 3) return cal_water_bill_3(unit_used, setting);
  return cal_water_bill_2(unit_used, setting);
};

// นับจำนวน "วันตัดรอบ" ที่ผ่านไปแล้ว ตั้งแต่วันที่ออกบิลจนถึงวันนี้
//   startDateStr = วันที่ออกบิล
//   specialDays  = วันที่ในเดือนที่ถือเป็นวันตัดรอบ เช่น [5, 20]
// ใช้หาว่าบิลใบนี้ค้างมากี่รอบ (เอาไปคูณเบี้ยปรับ / เช็คว่าถึงรอบตัดมิเตอร์หรือยัง)
export function countPassedSpecialDays(startDateStr, specialDays) {
  const startDate = new Date(startDateStr);
  const endDate = new Date(); // วันนี้
  let count = 0;

  let year = startDate.getFullYear();
  let month = startDate.getMonth(); // 0 = มกราคม

  while (year < endDate.getFullYear() || (year === endDate.getFullYear() && month <= endDate.getMonth())) {
    for (const day of specialDays) {
      const specialDate = new Date(year, month, day);
      if (specialDate >= startDate && specialDate <= endDate) {
        count++;
      }
    }
    // ข้ามไปเดือนถัดไป
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
  }

  return count;
}

// ยอดฐานของบิล 1 ใบ (ยังไม่รวมเบี้ยปรับและค่าต่อกลับมิเตอร์)
// NOTE ยอดฐานไม่รวม meter_connect_cost เพราะค่าต่อกลับมิเตอร์ถูกคำนวณใหม่ในชั้นล่าง
// แล้วบวกกลับเข้า total_amount ทีหลัง (ถ้าบวกค่าเดิมตรงนี้ด้วยจะคิดซ้ำทุกครั้งที่รันใหม่)
const base_amount = (item) => billTotal(item);

// คำนวณ "เบี้ยปรับ" ของทุกบิลในห้องเดียว
// เบี้ยปรับ = ยอดฐาน x จำนวนรอบที่ค้าง x (อัตราต่อปี / 100) / 12
// อัตราต่อปีเลือกจากช่วง month_start..month_end ที่จำนวนรอบค้างตกอยู่
const apply_penalty = (room, penalty_month_list) => {
  for (let item of room) {
    for (let p of penalty_month_list) {
      if (p.month_start <= item.total_month_late && item.total_month_late <= p.month_end) {
        // เบี้ยปรับ ปัดครึ่งขึ้น 2 ตำแหน่ง เหมือนยอดเงินจุดอื่นในระบบ
        item.late_payment = roundToTwo((item.total_amount * item.total_month_late * (p.penalty_year_rate / 100)) / 12);
        item.total_amount = Number(item.total_amount) + Number(item.late_payment);
        // NOTE เดิมไม่มี break — ถ้าตั้งช่วงเดือนซ้อนทับกัน (เช่น 1-6 กับ 6-999)
        //      บิลที่ตกอยู่ในช่วงซ้อนจะถูกคิดเบี้ยปรับซ้ำ ยอดบวกเข้าไป 2 รอบ
        //      หยุดที่ช่วงแรกที่ตรง = 1 บิลคิดเบี้ยปรับครั้งเดียวเสมอ
        break;
      }
    }
  }
};

// คำนวณ "ค่าต่อกลับมิเตอร์" ของห้องเดียว
// คิดกับบิลใบแรกที่ค้างเกินจำนวนรอบที่ตั้งไว้ (meter_cut_month) เท่านั้น ใบถัดไปไม่คิดซ้ำ
// status_meter: 1 = ยังไม่ถูกตัด, 2 = ถูกตัด/ต้องจ่ายค่าต่อกลับ
const apply_meter_cost = (room, meter_convergence, meter_cut_month, meter_cut_date) => {
  const meter_cost = Number(meter_convergence) || 0;
  let check_meter = false;

  for (let item of room) {
    if (check_meter === false) {
      // +1 เพราะนับรอบเดือนที่ออกบิลด้วย
      const month = 1 + countPassedSpecialDays(item.datetime_create, [meter_cut_date]);
      if (meter_cut_month < month) {
        item.meter_connect_cost = meter_cost;
        // NOTE บวกเข้ายอดรวม ให้ตรงกับสูตรฝั่ง backend: ยอดฐาน + เบี้ยปรับ + ค่าต่อมิเตอร์
        item.total_amount = Number(item.total_amount) + meter_cost;
        item.status_meter = 2;
        check_meter = true;
      } else {
        item.meter_connect_cost = 0;
        item.status_meter = 1;
        check_meter = false;
      }
    }
  }
};

// คำนวณยอดค้างชำระใหม่ทั้งโครงการ (หรือเฉพาะบ้านเลขที่ที่ส่งมา) แล้วอัปเดตกลับ backend
// ขั้นตอน: ดึงบิลค้าง + ตั้งค่าโครงการ → คิดเบี้ยปรับ → คิดค่าต่อกลับมิเตอร์ → PUT กลับทีละ 1000 รายการ
export const calculate_water = async (address_number) => {
  try {
    const [result, setting] = await Promise.all([
      GET(tokens, "v1/TH/record-receipt/filter-search/page/0/" + getStorage("project_id"), { address_number: address_number || "", building: "" }),
      GET(tokens, "v1/TH/water-manage/detail/" + getStorage("water_id"), null),
    ]);
    // ดึงข้อมูลไม่สำเร็จ (เน็ตหลุด / server ไม่ว่าง / คนอื่นกำลังคำนวณอยู่)
    // ต้องคืน false ให้ชัดเจน ไม่ใช่ undefined — ผู้เรียกจะได้แยกออกว่าล้มเหลว
    if (!result?.status || !setting?.data?.data) return false;

    const { penalty_day_list, penalty_month_list, meter_convergence, meter_cut_month, meter_cut_date } = setting.data.data;

    // เตรียมข้อมูลบิลแต่ละใบ: เรียงตามห้อง+วันที่ แล้วคำนวณยอดฐาน/จำนวนรอบที่ค้าง
    let obj = result?.data?.data_water;
    obj = obj.sort(fieldSorter(["address_number", "datetime_create"]));
    obj = obj.map((item) => ({
      address_number: item.address_number,
      datetime_create: item.datetime_create,
      record_water_id: item.record_water_id,
      total_amount: base_amount(item),
      late_payment: 0,
      total_month_late: countPassedSpecialDays(item.datetime_create, penalty_day_list),
      meter_connect_cost: 0,
      status_meter: 1,
    }));

    // จัด array ให้อยู่ในรูปตามแต่ละห้อง (เบี้ยปรับ/ค่ามิเตอร์ ต้องคิดแยกรายห้อง)
    let array = Object.values(
      obj.reduce((acc, item) => {
        if (!acc[item.address_number]) acc[item.address_number] = [];
        acc[item.address_number].push(item);
        return acc;
      }, {})
    );

    // คำนวณทีละห้อง
    for (let room of array) {
      apply_penalty(room, penalty_month_list);
      room = room.sort(fieldSorter(["address_number", "datetime_create"]));
      apply_meter_cost(room, meter_convergence, meter_cut_month, meter_cut_date);
    }

    // ส่งกลับ backend ทีละก้อน ก้อนละ 1000 รายการ (กัน payload ใหญ่เกินจน request ล้ม)
    // NOTE ส่งเบี้ยปรับไปทั้งชื่อ late_payment และ total_cost_late
    //      backend รุ่นเก่าอ่าน w["total_cost_late"] ตรงๆ ถ้าไม่มีคีย์นี้จะ KeyError แล้วคืน
    //      "ระบบขัดข้อง" ทั้งที่ข้อมูลถูกต้อง · รุ่นใหม่อ่าน late_payment ก่อนแล้วค่อย fallback
    array = array.flat().map((item) => ({ ...item, total_cost_late: item.late_payment }));
    const chunks = [];
    for (let i = 0; i < array.length; i += 1000) {
      chunks.push(array.slice(i, i + 1000));
    }
    const saved = await Promise.all(chunks.map((chunk) => PUT(tokens, "v1/TH/record-water/re-update/" + getStorage("project_id"), { water_list: chunk })));

    // NOTE เดิมไม่เช็คผลลัพธ์ของ PUT เลย — ถ้าก้อนใดก้อนหนึ่งบันทึกไม่สำเร็จ (เช่น คนอื่นกำลังคำนวณอยู่)
    //      ฟังก์ชันนี้ยังคืน true อยู่ดี ระบบเลยจดว่า sync สำเร็จแล้วและไม่ลองใหม่อีกทั้งวัน
    //      ทั้งที่ยอดค้าง/เบี้ยปรับบางส่วนยังไม่ถูกอัปเดต
    if (saved.some((r) => !r?.status)) return false;

    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};
